import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { LDAP_MANAGEMENT, TITLES } from 'locales/locales'
import {
  useLazyGetLdapSyncServerListQuery,
  useCreateAndUpdateLdapUsersMutation,
  useCreateAndUpdateLdapRolesMutation,
  useCreateAndUpdateLdapGroupsMutation,
  useCreateAndUpdateLdapRoomsMutation,
  useCreateAndUpdateLdapSubscriptionsMutation, useSendLdapSyncLogMutation,
  useStartLdapSyncMutation,
  useStopLdapSyncMutation,

} from 'app/services/system.service'

import SettingsHeader from 'features/settings/SettingsHeader'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import { useAppSelector } from '../../../../app/hooks'
import { IRolePermissions, SettingsPermissions } from 'types/role.types'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'

interface LdapSyncLogItem {
  type: string;
  details: {
    action: string;
    syncDate: string | number | Date;
  };
  createdAt: string | number | Date;
}

interface ILogData {
  type: string;
  details: {
    action: string;
    syncDate: Date;
  };
  createdAt: Date;
}

const LdapManagement = () => {
  const { t } = useTranslation()
  const [sendLdapSyncLog] = useSendLdapSyncLogMutation()
  const [getLdapSyncList] = useLazyGetLdapSyncServerListQuery()
  const [createAndUpdateLdapUsers] = useCreateAndUpdateLdapUsersMutation()
  const [createAndUpdateLdapRoles] = useCreateAndUpdateLdapRolesMutation()
  const [createAndUpdateLdapGroups] = useCreateAndUpdateLdapGroupsMutation()
  const [createAndUpdateLdapRooms] = useCreateAndUpdateLdapRoomsMutation()
  const [createAndUpdateLdapSubscriptions] = useCreateAndUpdateLdapSubscriptionsMutation()
  const [startLdapSync] = useStartLdapSyncMutation()
  const [stopLdapSync] = useStopLdapSyncMutation()
  

  const [loading, setLoading] = useState(false)
  const [currentData, setCurrentData] = useState<LdapSyncLogItem[]>([])

  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)

  const hasLdapUpdatePermission = isFlagSetIn(
    [SettingsPermissions.UPDATE_LDAP_SETTINGS],
    permissions.settings,
  )

  useEffect(() => {
    fetchLdapSync()
  }, [])


  const createAndSendLog = async (action: string, syncType: string) => {
    const logData: ILogData = {
      type: syncType,
      details: {
        action: action,
        syncDate: new Date(),
      },
      createdAt: new Date(),
    }

    await sendLdapSyncLog(logData)
  }

  const fetchLdapSync = async () => {
    try {
      const response = await getLdapSyncList({}).unwrap()
      if (response && response.length > 0) {
        const formattedData: LdapSyncLogItem[] = response.map((item: LdapSyncLogItem) => ({
          ...item,
          createdAt: new Date(item?.createdAt || "").toLocaleString(),
          details: {
            ...item.details,
            syncDate: new Date(item?.details?.syncDate || "").toLocaleString(),
          },
        }))
        setCurrentData(formattedData.reverse());
      }
    } catch (e) {
      console.warn('ERROR', e);
    }
  }


  const handleLdapOperation = async (
    operation: () => Promise<{ data: any } | { error: any }>,
    logType: string,
    actionType:string
  ) => {
    setLoading(true);
    try {
      await operation();
      await createAndSendLog(actionType, logType);
      await fetchLdapSync();
    } catch (error) {
      console.error('LDAP Server senkronizasyonu hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncTypeSwitch:any = (type:string) => {
    switch (type) {
      case 'start':
        return t(LDAP_MANAGEMENT.START_SYNC)
      case 'stop':
        return t(LDAP_MANAGEMENT.STOP_SYNC)
      case 'groups':
        return t(LDAP_MANAGEMENT.GROUP_SYNC)
      case 'user':
        return t(LDAP_MANAGEMENT.USER_SYNC)
      case 'roles':
        return t(LDAP_MANAGEMENT.ROLE_SYNC)
      case 'rooms':
        return t(LDAP_MANAGEMENT.ROOM_SYNC)
      case 'subscriptions':
        return t(LDAP_MANAGEMENT.SUB_SYNC)
      default:
        return t(LDAP_MANAGEMENT.UNKNOWING_SYNC)
    }
  }

  return (
    <Wrapper>
      {loading && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
          <div className="spinner-text">{t(LDAP_MANAGEMENT.UPDATING_LDAP)}</div>
        </div>
      )}
      <TopContainer>
        <SettingsHeader title={t(TITLES.LDAP_SETTINGS_TITLE)} />
        {hasLdapUpdatePermission ? <ButtonContainer>
          <LdapButton onClick={ () =>   handleLdapOperation(() => startLdapSync({}), 'start', "Functioning Consistently")}>{t(LDAP_MANAGEMENT.START_SYNC)}</LdapButton>
          <LdapButton onClick={ () =>   handleLdapOperation(() => stopLdapSync({}), 'stop', "Functioning Consistently")}>{t(LDAP_MANAGEMENT.STOP_SYNC)}</LdapButton>
          <LdapButton onClick={ () =>   handleLdapOperation(() => createAndUpdateLdapUsers({}), 'user', "Worked Once")}>{t(LDAP_MANAGEMENT.ADD_UPDATE_LDAP_USERS)}</LdapButton>
          <LdapButton onClick={ () =>   handleLdapOperation(() => createAndUpdateLdapRoles({}), 'roles', "Worked Once")}>{t(LDAP_MANAGEMENT.ADD_UPDATE_LDAP_ROLES)}</LdapButton>
          <LdapButton onClick={ () =>   handleLdapOperation(() => createAndUpdateLdapGroups({}), 'groups', "Worked Once")}>{t(LDAP_MANAGEMENT.ADD_UPDATE_LDAP_GROUPS)}</LdapButton>
          <LdapButton onClick={ () =>   handleLdapOperation(() => createAndUpdateLdapRooms({}), 'rooms', "Worked Once")}>{t(LDAP_MANAGEMENT.ADD_UPDATE_LDAP_ROOMS)}</LdapButton>
          <LdapButton onClick={ () =>   handleLdapOperation(() => createAndUpdateLdapSubscriptions({}), 'subscriptions', "Worked Once")}>{t(LDAP_MANAGEMENT.ADD_UPDATE_LDAP_SUBSCRIPTIONS)}</LdapButton>
       </ButtonContainer> : null}
        {/* <CustomButton
          textColor="white"
          onClick={() => setIsCreateModalVisible(true)}
          text={t(LDAP_MANAGEMENT.NEW_REGISTRY)}
        /> */}
      </TopContainer>

      <Grid data={currentData} style={{ height: '700px' }}>
        <GridColumn
          field="type"
          title={t(LDAP_MANAGEMENT.TYPE)}
          cell={(props) => (
            <td>{syncTypeSwitch(props.dataItem.type)}</td>
          )}
        />
        <GridColumn
          field="action"
          title={t(LDAP_MANAGEMENT.ACTION)}
          cell={(props) => (
            <td>
              {props.dataItem?.details?.action === "Functioning Consistently" ? t(LDAP_MANAGEMENT.FUNCTIONING_CONSISTENTLY):t(LDAP_MANAGEMENT.WORKED_ONCE) }
            </td>
          )}
        />
        <GridColumn field="createdAt" title={t(LDAP_MANAGEMENT.SYNCHRONIZATION_DATE)} format="{0:yyyy-MM-dd HH:mm:ss}" />
      </Grid>

    </Wrapper>
  )
}

export default LdapManagement
export const Wrapper = styled.div`
    padding: 0px 30px;
    width: 100%;
    overflow: auto;

    &::-webkit-scrollbar {
        display: none;
    }
`
export const TopContainer = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    align-items: center;
`
const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;
    align-items: flex-start;
`

const LdapButton = styled.button`
    background-color: #fff;
    color: #1c1c1c;
    padding: 13px 20px;
    font-weight: 600;
    border: 1px solid #e1e1e1;
    border-radius: 5px;
    cursor: pointer;
    margin-left: 10px;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.06);
    transition: box-shadow 0.3s ease;

    &:hover {
        box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
`;
